import React, { useState, useEffect, useRef } from 'react';
import { getUsers, updateUserStatus, updateUserDetails, checkUserDuplicate } from '../../services/userService';

// Define the User interface to match the API response
interface User {
  _id: string;
  name: string;
  lastName: string;
  mobile: string;
  email: string;
  nationalId: string;
  status?: 'active' | 'suspended';
  userType: string;
  createdAt: string;
  updatedAt: string;
}

interface UserFormData {
  name: string;
  lastName: string;
  mobile: string;
  email: string;
  nationalId: string;
  password?: string;
  confirmPassword?: string;
  changePassword?: boolean;
}

interface PaginationData {
  total: number;
  totalPages: number;
  currentPage: number;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    totalPages: 1,
    currentPage: 1
  });
  const [itemsPerPage] = useState(10);

  // Filter states
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    lastName: '',
    mobile: '',
    email: '',
    nationalId: '',
    password: '',
    confirmPassword: '',
    changePassword: false
  });
  
  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Password change success popup state
  const [passwordChangePopup, setPasswordChangePopup] = useState<{
    open: boolean;
    mobile: string;
    password: string;
  }>({
    open: false,
    mobile: '',
    password: ''
  });
  
  // Duplicate validation errors
  const [duplicateErrors, setDuplicateErrors] = useState<{
    mobile?: string;
    email?: string;
    nationalId?: string;
  }>({});
  
  // Debounce timer refs for validation
  const validationTimers = useRef<{
    mobile?: NodeJS.Timeout;
    email?: NodeJS.Timeout;
    nationalId?: NodeJS.Timeout;
  }>({});

  // Fetch users from API on initial load and when pagination or filters change
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await getUsers({
          userType: 'User', // Filter by userType=User
          page: pagination.currentPage,
          limit: itemsPerPage,
          status: statusFilter !== 'all' ? statusFilter : undefined
        });
        
        if (response.success) {
          setUsers(response.data);
          setPagination({
            currentPage: response.pagination.currentPage,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          });
          setError('');
        } else {
          setError('خطا در دریافت اطلاعات کاربران');
        }
      } catch (err) {
        setError('خطا در ارتباط با سرور');

      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [pagination.currentPage, itemsPerPage, statusFilter]);

  // Filter users by search term locally (after they're fetched from API)
  const filteredUsers = users.filter(user => {
    const fullName = `${user.name || ''} ${user.lastName || ''}`;
    const matchesSearch = filter === '' || 
                        fullName.includes(filter) || 
                        user.mobile.includes(filter) || 
                        (user.email || '').includes(filter) || 
                        (user.nationalId || '').includes(filter);
    
    return matchesSearch;
  });

  // Change pagination page
  const changePage = (newPage: number) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination({...pagination, currentPage: newPage});
    }
  };

  // Suspend/Activate user
  const changeUserStatus = async (userId: string, newStatus: 'active' | 'suspended') => {
    try {
      const response = await updateUserStatus(userId, newStatus);
      
      if (response.success) {
        // Update the local state to reflect the change
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u._id === userId ? { ...u, status: newStatus } : u
          )
        );
        setError('');
      } else {
        setError('خطا در تغییر وضعیت کاربر');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');

    }
  };
  
  // Validate field for duplicates
  const validateField = async (fieldName: 'mobile' | 'email' | 'nationalId', value: string) => {
    // Clear previous error for this field
    setDuplicateErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
    
    // Clear existing timer for this field
    if (validationTimers.current[fieldName]) {
      clearTimeout(validationTimers.current[fieldName]);
    }
    
    // Skip validation if value is empty
    if (!value || value.trim() === '') {
      return;
    }
    
    // Debounce validation (wait 500ms after user stops typing)
    validationTimers.current[fieldName] = setTimeout(async () => {
      try {
        const checkData: any = { [fieldName]: value };
        if (selectedUser) {
          checkData.userId = selectedUser._id;
        }
        
        const result = await checkUserDuplicate(
          checkData.mobile,
          checkData.email,
          checkData.nationalId,
          checkData.userId
        );
        
        if (!result.success && result.errors[fieldName]) {
          setDuplicateErrors(prev => ({
            ...prev,
            [fieldName]: result.errors[fieldName]
          }));
        }
      } catch (err) {

      }
    }, 500);
  };
  
  // Close edit modal
  const closeEditModal = () => {
    setEditModalOpen(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError('');
    setDuplicateErrors({});
    // Clear all validation timers
    Object.values(validationTimers.current).forEach(timer => {
      if (timer) clearTimeout(timer);
    });
    validationTimers.current = {};
  };
  
  // Open edit modal with selected user data
  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      lastName: user.lastName || '',
      mobile: user.mobile || '',
      email: user.email || '',
      nationalId: user.nationalId || '',
      password: '',
      confirmPassword: '',
      changePassword: false
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setEditModalOpen(true);
  };
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Validate duplicate fields in real-time
    if (name === 'mobile' || name === 'email' || name === 'nationalId') {
      validateField(name, value);
    }
    
    // Clear password fields when unchecking changePassword
    if (name === 'changePassword' && !checked) {
      setFormData(prev => ({
        ...prev,
        changePassword: false,
        password: '',
        confirmPassword: ''
      }));
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  };
  
  // Generate random password (8 digits + 1 letter at the end)
  const generateRandomPassword = () => {
    const numbers = '0123456789';
    const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let password = '';
    
    // Generate 8 random digits
    for (let i = 0; i < 8; i++) {
      password += numbers[Math.floor(Math.random() * numbers.length)];
    }
    
    // Add 1 random letter at the end
    password += letters[Math.floor(Math.random() * letters.length)];
    
    setFormData(prev => ({
      ...prev,
      password: password,
      confirmPassword: password
    }));
  };
  
  // Submit edited user data
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    // Clear previous errors
    setError('');
    setDuplicateErrors({});
    
    // Validate duplicates before submit
    const duplicateCheck = await checkUserDuplicate(
      formData.mobile,
      formData.email,
      formData.nationalId,
      selectedUser._id
    );
    
    if (!duplicateCheck.success) {
      setDuplicateErrors(duplicateCheck.errors);
      const firstError = Object.values(duplicateCheck.errors)[0] as string | undefined;
      if (firstError) {
        setError(firstError);
      }
      return;
    }
    
    // Validate password if changePassword is checked
    if (formData.changePassword) {
      if (!formData.password || formData.password.length < 8) {
        setError('رمز عبور باید حداقل ۸ کاراکتر باشد');
        return;
      }
      
      if (formData.password !== formData.confirmPassword) {
        setError('رمز عبور و تکرار آن یکسان نیستند');
        return;
      }
    }
    
    try {
      const response = await updateUserDetails(selectedUser._id, formData);
      
      if (response.success) {
        // Update the local state (exclude password fields from display)
        const { password, confirmPassword, changePassword, ...displayData } = formData;
        setUsers(prevUsers =>
          prevUsers.map(u =>
            u._id === selectedUser._id ? { ...u, ...displayData } : u
          )
        );
        
        // If password was changed, show success popup
        if (formData.changePassword && formData.password) {
          setPasswordChangePopup({
            open: true,
            mobile: selectedUser.mobile,
            password: formData.password
          });
        }
        
        closeEditModal();
      } else {
        // Show error message from backend
        setError(response.message || 'خطا در ویرایش اطلاعات کاربر');
      }
    } catch (err: any) {
      // Handle unexpected errors (network errors, etc.)
      const errorMessage = err.response?.data?.message || err.message || 'خطا در ارتباط با سرور';
      setError(errorMessage);

    }
  };

  // Format a date string to Persian format (simple implementation)
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      // Format as YYYY/MM/DD (simplified - real implementation would use a proper Persian date library)
      return date.toLocaleDateString('fa-IR');
    } catch (error) {

      return dateString;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">کاربران سامانه</h1>
        
        {error && (
          <div className="bg-red-50 text-red-700 p-3 mb-4 rounded border border-red-200">
            {error}
          </div>
        )}
        
        <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between items-end">
          <div className="w-full md:w-1/3">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">جستجو</label>
            <input
              type="text"
              id="search"
              placeholder="جستجو بر اساس نام، موبایل، ایمیل یا کد ملی"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div className="w-full md:w-1/4">
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">همه</option>
              <option value="active">فعال</option>
              <option value="suspended">تعلیق شده</option>
            </select>
          </div>
          
          <div>
            <button className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
              خروجی اکسل
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-10 text-center text-gray-600">در حال بارگذاری...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-10 text-center text-gray-600">هیچ کاربری یافت نشد</div>
          ) : (
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">شماره موبایل</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ایمیل</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">کد ملی</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت نام</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user._id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{`${user.name || ''} ${user.lastName || ''}`}</td>
                    <td className="py-3 px-4 text-sm">{user.mobile}</td>
                    <td className="py-3 px-4 text-sm">{user.email || '-'}</td>
                    <td className="py-3 px-4 text-sm">{user.nationalId || '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(user.createdAt)}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.status === 'active' ? 'فعال' : 'تعلیق شده'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex space-x-2">
                        <button 
                          className={`ml-2 px-3 py-1 rounded text-xs ${user.status === 'active' ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                          onClick={() => changeUserStatus(user._id, user.status === 'active' ? 'suspended' : 'active')}
                        >
                          {user.status === 'active' ? 'تعلیق' : 'فعال سازی'}
                        </button>
                        <button 
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                          onClick={() => openEditModal(user)}
                        >
                          ویرایش
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {pagination.totalPages > 0 && (
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              نمایش {filteredUsers.length} کاربر از {pagination.total} کاربر
            </div>
            
            <div className="flex">
              <button 
                onClick={() => changePage(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1}
                className={`px-3 py-1 border border-gray-300 rounded-r-md ${pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
              >
                قبلی
              </button>
              
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const pageNum = pagination.currentPage <= 3 
                  ? i + 1 
                  : pagination.currentPage >= pagination.totalPages - 2 
                    ? pagination.totalPages - 4 + i 
                    : pagination.currentPage - 2 + i;
                
                if (pageNum > 0 && pageNum <= pagination.totalPages) {
                  return (
                    <button 
                      key={pageNum}
                      onClick={() => changePage(pageNum)}
                      className={`px-3 py-1 border-t border-b border-gray-300 ${pageNum === pagination.currentPage ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-100'}`}
                    >
                      {pageNum}
                    </button>
                  );
                }
                return null;
              })}
              
              <button 
                onClick={() => changePage(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
                className={`px-3 py-1 border border-gray-300 rounded-l-md ${pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
              >
                بعدی
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Edit User Modal */}
      {editModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">ویرایش اطلاعات کاربر</h3>
              <button 
                onClick={closeEditModal}
                className="text-gray-500 hover:text-gray-700"
                title="بستن"
                aria-label="بستن"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmitEdit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">نام</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="نام کاربر را وارد کنید"
                  title="نام کاربر"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">نام خانوادگی</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="نام خانوادگی کاربر را وارد کنید"
                  title="نام خانوادگی کاربر"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">موبایل</label>
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="شماره موبایل کاربر را وارد کنید"
                  title="شماره موبایل کاربر"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    duplicateErrors.mobile ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {duplicateErrors.mobile && (
                  <p className="mt-1 text-sm text-red-600">{duplicateErrors.mobile}</p>
                )}
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ایمیل <span className="text-gray-400 text-xs">(اختیاری)</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="ایمیل کاربر را وارد کنید"
                  title="ایمیل کاربر"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    duplicateErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {duplicateErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{duplicateErrors.email}</p>
                )}
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">کد ملی</label>
                <input
                  type="text"
                  name="nationalId"
                  value={formData.nationalId}
                  onChange={handleInputChange}
                  placeholder="کد ملی کاربر را وارد کنید"
                  title="کد ملی کاربر"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    duplicateErrors.nationalId ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {duplicateErrors.nationalId && (
                  <p className="mt-1 text-sm text-red-600">{duplicateErrors.nationalId}</p>
                )}
              </div>
              
              {/* Password Change Section */}
              <div className="mb-4 border-t pt-4">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    name="changePassword"
                    id="changePassword"
                    checked={formData.changePassword || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="changePassword" className="mr-2 block text-sm font-medium text-gray-700">
                    تغییر رمز عبور
                  </label>
                </div>
                
                {formData.changePassword && (
                  <>
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-sm font-medium text-gray-700">رمز عبور جدید</label>
                        <button
                          type="button"
                          onClick={generateRandomPassword}
                          className="text-xs text-blue-600 hover:text-blue-700 underline"
                        >
                          تولید رمز تصادفی
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password || ''}
                          onChange={handleInputChange}
                          placeholder="رمز عبور جدید (حداقل ۸ کاراکتر)"
                          title="رمز عبور جدید"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          title={showPassword ? "مخفی کردن رمز" : "نمایش رمز"}
                        >
                          {showPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">تکرار رمز عبور جدید</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={formData.confirmPassword || ''}
                          onChange={handleInputChange}
                          placeholder="رمز عبور جدید را مجددا وارد کنید"
                          title="تکرار رمز عبور جدید"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          title={showConfirmPassword ? "مخفی کردن رمز" : "نمایش رمز"}
                        >
                          {showConfirmPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  ذخیره تغییرات
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Password Change Success Popup */}
      {passwordChangePopup.open && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-green-600">رمز عبور با موفقیت تغییر یافت</h3>
              <button 
                onClick={() => setPasswordChangePopup({ open: false, mobile: '', password: '' })}
                className="text-gray-500 hover:text-gray-700"
                title="بستن"
                aria-label="بستن"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام کاربری (موبایل):</label>
                  <div className="text-lg font-semibold text-gray-900 bg-white border border-gray-300 rounded-md px-3 py-2">
                    {passwordChangePopup.mobile}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">رمز عبور جدید:</label>
                  <div className="text-lg font-mono font-semibold text-gray-900 bg-white border border-gray-300 rounded-md px-3 py-2">
                    {passwordChangePopup.password}
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">توجه:</span> لطفاً این اطلاعات را به کاربر اطلاع دهید.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setPasswordChangePopup({ open: false, mobile: '', password: '' })}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
